import { useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { useProjectsDataTable } from 'generated/resources/datatable.graphql'
import {
  CreateProjectDocument,
  CreateProjectMutation,
  CreateProjectMutationVariables,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import Upgrade from 'modules/core/components/Upgrade'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { UpsellProjectsImportModal } from '../../upsells/UpsellProjectsImportModal'
import AddProjectWizard from '../../wizards/AddProjectWizard'
import Actions from './Actions'
import { BillableFilter } from './Filters/BillableFilter'
import { ClientFilter } from './Filters/ClientFilter'
import { LegalEntityFilter } from './Filters/LegalEntityFilter'
import { StatusFilter } from './Filters/StatusFilter'
import NoProjectsMessage from './TableBody/NoProjectsMessage'
import { projectProjections } from './projections'

const MAX_AVATARS = 4

const ProjectsDataTable = ({
  canSeeCosts,
  canCreateProjects,
  canDeleteProjects,
  canManageProjects,
  hasBudgetFeature,
  hasBulkActions,
  hasClientsFeature,
  hasCostsFeature,
  hasExpensesModule,
  hasFlexibleTracking,
  hasImportProjectsEnabled,
  hasSubprojectsFeature,
  hasTimeTrackingSection,
}: {
  canSeeCosts: boolean
  canCreateProjects: boolean
  canDeleteProjects: boolean
  canManageProjects: boolean
  hasBudgetFeature: boolean
  hasBulkActions: boolean
  hasClientsFeature: boolean
  hasCostsFeature: boolean
  hasExpensesModule: boolean
  hasFlexibleTracking: boolean
  hasImportProjectsEnabled: boolean
  hasSubprojectsFeature: boolean
  hasTimeTrackingSection: boolean
}) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  const {
    employee,
    company: { id: companyId },
    access: { locale: accessLocale },
  } = useCurrentSession()
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [showCreateProjectModal, setShowCreateProjectModal] = useState(false)

  const [createProject] = useMutation(CreateProjectDocument, {
    extractorFn: (data: CreateProjectMutation) =>
      data.projectManagementMutations.createProjectmanagementProject,
  })

  const wrappedCreateProject = (dto: CreateProjectMutationVariables) =>
    createProject({ variables: dto })

  const variables = {
    nameOrCode: debouncedSearch,
    maxProjectWorkers: MAX_AVATARS,
    hasBillable: hasCostsFeature,
    hasClient: hasClientsFeature,
    hasCost: canSeeCosts,
    hasSpendings: canSeeCosts && hasExpensesModule,
    hasFixedTimeBudget: hasBudgetFeature,
    companyId,
  }

  const source = useProjectsDataTable(
    projectProjections({
      locale: accessLocale,
      hasBillable: hasCostsFeature,
      canManageProjects,
      hasFixedTimeBudget: hasBudgetFeature,
      hasClients: hasClientsFeature,
      hasCost: canSeeCosts,
      hasSubprojectsFeature,
      canCreateProjects,
      canDeleteProjects,
    }),
    {
      variables,
    }
  )

  const canExportData =
    canManageProjects &&
    !!employee &&
    (hasTimeTrackingSection || hasExpensesModule || hasFlexibleTracking)

  const filters = compact([
    StatusFilter(),
    hasCostsFeature ? BillableFilter() : undefined,
    LegalEntityFilter(),
    hasClientsFeature ? ClientFilter(companyId) : undefined,
  ])

  const importProjectsAction = () => {
    if (hasImportProjectsEnabled) {
      track(trackingEvents.projectManagement.importProjects.importProjectsStart)
      navigate(navigation.projects.list.import.fullPath())
    } else {
      track(trackingEvents.projectManagement.importProjects.upgrade.visitUpgradeModal)
      setShowUpgradeModal(true)
    }
  }

  const onCreateProjectSuccess = (projectId: number) => {
    setShowCreateProjectModal(false)
    navigate(navigation.projects.id.fullPath({ id: projectId }))
  }

  return (
    <>
      <DataTable.Provider
        source={source}
        pagination={{
          kind: 'offset',
          itemsPerPage: 12,
          infiniteScroll: false,
        }}
        sort={{
          path: 'status',
        }}
        filters={filters}
      >
        <Box gap="s16">
          <DataTable.HeadBar
            disableExport
            customPopoverOptions={
              <>
                {canCreateProjects && (
                  <PopoverMenu.TouchableOption
                    title={`${i18n.t(
                      'project_management.table.projects_list.table_options.import.title'
                    )}`}
                    description={`${i18n.t(
                      'project_management.table.projects_list.table_options.import.description'
                    )}`}
                    onClick={() => importProjectsAction()}
                    extraContent={!hasImportProjectsEnabled ? <Upgrade.Tiny /> : null}
                  />
                )}
                {canExportData && (
                  <PopoverMenu.TouchableOption
                    title={`${i18n.t(
                      'project_management.table.projects_list.table_options.export.title'
                    )}`}
                    description={`${i18n.t(
                      'project_management.table.projects_list.table_options.export.description'
                    )}`}
                    onClick={() => {
                      track(trackingEvents.projectManagement.projects.downloadProjectsReportStart)
                      navigate(navigation.projects.list.export.fullPath())
                    }}
                  />
                )}
              </>
            }
            actions={
              <Actions
                canCreateProjects={canCreateProjects}
                hasBulkActions={hasBulkActions}
                hasClientsFeature={hasClientsFeature}
                onClickCreateProject={() => setShowCreateProjectModal(true)}
              />
            }
            search={{
              placeholder: i18n.t('common.search_by'),
              value: search,
              onChange: onSearch,
            }}
          />
          <DataTable.Table
            emptyState={
              <NoProjectsMessage
                canCreateProjects={canCreateProjects}
                onClickCreateProject={() => setShowCreateProjectModal(true)}
              />
            }
            autoWidth
            selectable={hasBulkActions ? () => true : undefined}
          />
        </Box>
        <Outlet context={{ hasExpensesModule, hasFlexibleTracking, hasImportProjectsEnabled }} />
      </DataTable.Provider>
      {showUpgradeModal && <UpsellProjectsImportModal onClose={() => setShowUpgradeModal(false)} />}
      {showCreateProjectModal && (
        <AddProjectWizard
          onClose={() => setShowCreateProjectModal(false)}
          onSuccess={onCreateProjectSuccess}
          create={wrappedCreateProject}
        />
      )}
    </>
  )
}

export default observer(ProjectsDataTable)
